import { useMemo, useState, useEffect, type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import BOMNavigation from "./BOMNavigation";
import {
  Section,
  SectionHeader,
  NoDataText,
} from "../../components/SectionComponents";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  TableHeader,
  TableRow,
  OldValue,
  ChangePill,
  SectionTitle,
  Footer,
} from "./DiffComponents";
import {
  diffMetadata,
  diffAttributes,
  diffChildren,
  diffProcesses,
  buildDiffTree,
  findDiffNodeByPath,
  getItemChangeCount,
  collectDiffPaths,
} from "./itemReplacementDiff";

const ReplacementBody = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));

const LeftPanel = styled(Box)({
  flex: "0 0 25%",
  overflow: "auto",
});

const RightPanel = styled(Box)({
  flex: "0 0 75%",
  overflow: "auto",
});

type ReplacementDiffModalProps = {
  open: boolean;
  title: string;
  description?: string;
  currentItem: ItemWithBOM;
  replacementItem: ItemWithBOM | null;
  preDiffContent?: ReactNode;
  emptyState?: ReactNode;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const ReplacementDiffModal = ({
  open,
  title,
  description,
  currentItem,
  replacementItem,
  preDiffContent,
  emptyState,
  confirmDisabled,
  onCancel,
  onConfirm,
}: ReplacementDiffModalProps) => {
  const [activePath, setActivePath] = useState("");

  useEffect(() => {
    if (open) {
      setActivePath("");
    }
  }, [open, replacementItem]);

  const diffRoot = useMemo(() => {
    if (!replacementItem) return null;
    return buildDiffTree(currentItem, replacementItem, "");
  }, [currentItem, replacementItem]);

  const activeNode = useMemo(() => {
    if (!diffRoot) return null;
    return findDiffNodeByPath(diffRoot, activePath) || diffRoot;
  }, [diffRoot, activePath]);

  const sections = useMemo(() => {
    if (!activeNode) return null;
    return {
      metadata: diffMetadata(activeNode.oldItem, activeNode.newItem),
      attributes: diffAttributes(activeNode.oldItem, activeNode.newItem),
      processes: diffProcesses(activeNode.oldItem, activeNode.newItem),
      children: diffChildren(activeNode.oldItem, activeNode.newItem),
    };
  }, [activeNode]);

  const diffPaths = useMemo(() => {
    return collectDiffPaths(diffRoot);
  }, [diffRoot]);

  const isReady = Boolean(replacementItem && activeNode && sections);
  const totalChanges = useMemo(() => {
    if (!replacementItem) return 0;
    return getItemChangeCount(currentItem, replacementItem);
  }, [currentItem, replacementItem]);
  const hasNoChanges = isReady && totalChanges === 0;

  const showEmptyState = !replacementItem && Boolean(emptyState);

  return (
    <Modal open={open} onClose={onCancel} maxWidth="xl" fullWidth>
      <ModalContent>
        <ModalHeader>
          <Typography variant="h6">{title}</Typography>
          {description ? (
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          ) : null}
        </ModalHeader>

        <ModalBody>
          {preDiffContent}
          {showEmptyState ? (
            <Box>{emptyState}</Box>
          ) : (
            <ReplacementBody>
              <LeftPanel>
                {replacementItem ? (
                  <BOMNavigation
                    condition={replacementItem}
                    activeItem={replacementItem}
                    activePathOverride={activePath}
                    diffPaths={diffPaths}
                    onItemClick={(item) => {
                      const findPath = (
                        root: ItemWithBOM,
                        target: ItemWithBOM,
                        path: number[] = []
                      ): number[] | null => {
                        if (root === target) return path;
                        for (let i = 0; i < root.bom.length; i++) {
                          const found = findPath(
                            root.bom[i],
                            target,
                            [...path, i]
                          );
                          if (found) return found;
                        }
                        return null;
                      };

                      const path = findPath(replacementItem, item);
                      setActivePath(path ? path.join(">") : "");
                    }}
                  />
                ) : (
                  <NoDataText>Loading replacement item...</NoDataText>
                )}
              </LeftPanel>
              <RightPanel>
                {!isReady ? (
                  <NoDataText>Loading differences...</NoDataText>
                ) : (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Section>
                      <SectionHeader>
                        <SectionTitle>
                          <span>Metadata</span>
                          <ChangePill>
                            {sections!.metadata.changeCount} change
                            {sections!.metadata.changeCount === 1 ? "" : "s"}
                          </ChangePill>
                        </SectionTitle>
                      </SectionHeader>
                      {sections!.metadata.rows.length === 0 ? (
                        <NoDataText>No metadata changes</NoDataText>
                      ) : (
                        <>
                          <TableHeader>
                            <span>Name</span>
                            <span>Old value</span>
                            <span>New value</span>
                          </TableHeader>
                          {sections!.metadata.rows.map((row) => (
                            <TableRow key={`metadata-${row.label}`}>
                              <span>{row.label}</span>
                              <OldValue>{row.oldValue || ""}</OldValue>
                              <span>{row.newValue || ""}</span>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </Section>

                    <Section>
                      <SectionHeader>
                        <SectionTitle>
                          <span>Attributes</span>
                          <ChangePill>
                            {sections!.attributes.changeCount} change
                            {sections!.attributes.changeCount === 1
                              ? ""
                              : "s"}
                          </ChangePill>
                        </SectionTitle>
                      </SectionHeader>
                      {sections!.attributes.rows.length === 0 ? (
                        <NoDataText>No attribute changes</NoDataText>
                      ) : (
                        <>
                          <TableHeader>
                            <span>Name</span>
                            <span>Old value</span>
                            <span>New value</span>
                          </TableHeader>
                          {sections!.attributes.rows.map((row) => (
                            <TableRow key={`attribute-${row.label}`}>
                              <span>{row.label}</span>
                              <OldValue>{row.oldValue || ""}</OldValue>
                              <span>{row.newValue || ""}</span>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </Section>

                    <Section>
                      <SectionHeader>
                        <SectionTitle>
                          <span>Processes</span>
                          <ChangePill>
                            {sections!.processes.changeCount} change
                            {sections!.processes.changeCount === 1 ? "" : "s"}
                          </ChangePill>
                        </SectionTitle>
                      </SectionHeader>
                      {sections!.processes.rows.length === 0 ? (
                        <NoDataText>No process changes</NoDataText>
                      ) : (
                        <>
                          <TableHeader>
                            <span>Name</span>
                            <span>Old value</span>
                            <span>New value</span>
                          </TableHeader>
                          {sections!.processes.rows.map((row, index) => (
                            <TableRow key={`process-${row.label}-${index}`}>
                              <span>{row.label}</span>
                              <OldValue>{row.oldValue || ""}</OldValue>
                              <span>{row.newValue || ""}</span>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </Section>

                    <Section>
                      <SectionHeader>
                        <SectionTitle>
                          <span>Children</span>
                          <ChangePill>
                            {sections!.children.changeCount} change
                            {sections!.children.changeCount === 1 ? "" : "s"}
                          </ChangePill>
                        </SectionTitle>
                      </SectionHeader>
                      {sections!.children.rows.length === 0 ? (
                        <NoDataText>No child changes</NoDataText>
                      ) : (
                        <>
                          <TableHeader>
                            <span>Name</span>
                            <span>Old value</span>
                            <span>New value</span>
                          </TableHeader>
                          {sections!.children.rows.map((row, index) => (
                            <TableRow key={`child-${row.label}-${index}`}>
                              <span>{row.label}</span>
                              <OldValue>{row.oldValue || ""}</OldValue>
                              <span>{row.newValue || ""}</span>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </Section>
                  </Box>
                )}
              </RightPanel>
            </ReplacementBody>
          )}
        </ModalBody>

        <Footer>
          <Button color="tertiary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={onConfirm}
            disabled={!replacementItem || hasNoChanges || confirmDisabled}
          >
            Confirm All Changes ({totalChanges})
          </Button>
        </Footer>
      </ModalContent>
    </Modal>
  );
};

export default ReplacementDiffModal;
